import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Pencil, Trash2, Plus, Mail, RefreshCw, Save } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import auctionViewerService, { AuctionViewerRecord } from '../../../services/auctionViewerService';
import CompanyViewerFields, { CompanyViewerValue, EMPTY_VIEWER, VIEWER_EMAIL_RE } from './CompanyViewerFields';
import './CompanyViewerFields.css';

interface Props {
  auctionId: string | number;
  // Completed/cancelled auctions can't be given a new viewer (the API agrees).
  ended?: boolean;
}


// Lives on the channel partner's own auction page: shows who the company
// viewer (2nd auctioneer) is, and lets the partner fix a wrong number, swap
// the person, or remove access. Renders nothing for anyone who isn't a
// channel partner, so it's safe to drop into a shared page.
const CompanyViewerCard: React.FC<Props> = ({ auctionId, ended = false }) => {
  const { user } = useAuth();
  const isPartner = user?.role === 'channel_partner';

  const [viewer, setViewer] = useState<AuctionViewerRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<CompanyViewerValue>(EMPTY_VIEWER);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setViewer(await auctionViewerService.getForAuction(auctionId));
    } catch {
      setViewer(null);
    } finally {
      setLoading(false);
    }
  }, [auctionId]);

  useEffect(() => {
    if (isPartner) load();
  }, [isPartner, load]);

  if (!isPartner) return null;

  const startEdit = () => {
    setForm(
      viewer
        ? { email: viewer.email || '' }
        : EMPTY_VIEWER
    );
    setErrors({});
    setMessage(null);
    setEditing(true);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!VIEWER_EMAIL_RE.test(form.email.trim())) errs.email = 'Enter a valid email address';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage(null);
    try {
      const result = await auctionViewerService.setForAuction(auctionId, {
        email: form.email.trim().toLowerCase(),
      });
      setViewer(result.viewer);
      setEditing(false);
      const notified = result.notifications.email === 'sent';
      setMessage({
        type: 'success',
        text: notified
          ? "The company viewer can now watch this auction live. They've been notified."
          : "The company viewer can now watch this auction live. We couldn't notify them automatically, so please share the login details yourself.",
      });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Could not save the company viewer' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Remove this company viewer? They will immediately lose access to this auction.')) return;
    setRemoving(true);
    setMessage(null);
    try {
      await auctionViewerService.removeForAuction(auctionId);
      setViewer(null);
      setMessage({ type: 'success', text: 'Company viewer removed.' });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Could not remove the viewer' });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="auction-details-card">
      <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Eye className="w-5 h-5" />
        Company's 2nd Auctioneer
        <span className="cv-badge">View only</span>
      </h2>

      {message && (
        <p
          className="cv-note cv-pad"
          style={{
            marginTop: 0,
            marginBottom: '1rem',
            color: message.type === 'error' ? 'var(--error-color, #ef4444)' : 'var(--text-primary)',
            borderColor: message.type === 'error' ? 'var(--error-color, #ef4444)' : undefined,
          }}
        >
          {message.text}
        </p>
      )}

      {loading ? (
        <p className="cv-note cv-pad" style={{ marginTop: 0 }}>
          Loading...
        </p>
      ) : editing ? (
        <>
          <div className="cv-pad">
            <CompanyViewerFields value={form} onChange={setForm} errors={errors} disabled={saving} />
          </div>
          <div className="cv-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {viewer ? 'Save Changes' : 'Add Viewer'}
            </button>
          </div>
        </>
      ) : viewer ? (
        <>
          <div className="auction-info-grid">
            <div className="info-item">
              <label className="info-label">
                <Mail className="w-4 h-4 inline cv-icon-label" />
                Login Email
              </label>
              <div className="info-value">
                {viewer.email || (viewer.phone_number ? `Phone ${viewer.phone_number} (no email on file)` : '—')}
              </div>
            </div>
          </div>
          <p className="cv-note cv-pad">
            They can watch this auction live (countdown, bids and ranking) but can't bid, change it, or open reports.
          </p>
          {!ended && (
            <div className="cv-actions">
              <button type="button" className="btn btn-secondary" onClick={startEdit} disabled={removing}>
                <Pencil className="w-4 h-4" /> Replace
              </button>
              <button type="button" className="btn btn-danger" onClick={remove} disabled={removing}>
                {removing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Remove
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p className="cv-note cv-pad" style={{ marginTop: 0 }}>
            No company viewer yet. Add the company's contact so they can follow this auction live, view-only.
          </p>
          {!ended && (
            <div className="cv-actions cv-actions-start">
              <button type="button" className="btn btn-primary" onClick={startEdit}>
                <Plus className="w-4 h-4" /> Add Company Viewer
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompanyViewerCard;
